import React, { useEffect, useId, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import './ds.css';

const GAP = 6;          // between the trigger and the panel
const EDGE = 8;         // the panel never touches the window's edge

/**
 * Popover / dropdown menu anchored to a trigger.
 * - trigger: render prop ({ open, toggle, props }) => element; spread `props` on the button
 * - closes on outside click and Escape, returns focus to the trigger
 * - align: 'start' | 'end'
 * - the panel lives on the page itself (a portal, fixed to the window): a table's scroll box or a
 *   card never cuts it; it opens upward when there is no room below, and follows the trigger on
 *   scroll / resize. Inside a dialog it stays above the dialog.
 */
export default function Popover({ trigger, children, align = 'end', width = 240, className = '', label, onOpenChange }) {
    const [open, setOpen] = useState(false);
    const [place, setPlace] = useState(null);
    const rootRef = useRef(null);
    const triggerRef = useRef(null);
    const panelRef = useRef(null);
    const uid = useId();
    const panelId = `pop-${uid}`;

    const setOpenState = (next) => {
        setOpen(next);
        if (onOpenChange) onOpenChange(next);
    };

    // where the panel goes: under the trigger, or above it when the room below is too small
    useLayoutEffect(() => {
        if (!open) { setPlace(null); return undefined; }
        const measure = () => {
            const anchor = triggerRef.current || rootRef.current;
            if (!anchor) return;
            const r = anchor.getBoundingClientRect();
            const vw = window.innerWidth;
            const vh = window.innerHeight;
            const w = Math.min(Number(width) || 240, vw - 2 * EDGE);
            const h = panelRef.current ? panelRef.current.offsetHeight : 0;
            const left = Math.max(EDGE, Math.min(align === 'end' ? r.right - w : r.left, vw - w - EDGE));
            const below = vh - r.bottom - GAP - EDGE;
            const above = r.top - GAP - EDGE;
            const up = h > below && above > below;
            setPlace({ left, width: w, up, top: up ? undefined : r.bottom + GAP,
                bottom: up ? vh - r.top + GAP : undefined, room: Math.max(160, up ? above : below) });
        };
        measure();
        const again = requestAnimationFrame(measure);          // once the panel has its height
        const grow = typeof ResizeObserver !== 'undefined' ? new ResizeObserver(measure) : null;
        if (grow && panelRef.current) grow.observe(panelRef.current);
        window.addEventListener('scroll', measure, true);
        window.addEventListener('resize', measure);
        return () => {
            cancelAnimationFrame(again);
            if (grow) grow.disconnect();
            window.removeEventListener('scroll', measure, true);
            window.removeEventListener('resize', measure);
        };
    }, [open, align, width]);

    useEffect(() => {
        if (!open) return undefined;
        const onDown = (e) => {
            const inside = (rootRef.current && rootRef.current.contains(e.target)) || (panelRef.current && panelRef.current.contains(e.target));
            if (!inside) setOpenState(false);
        };
        const onKey = (e) => {
            if (e.key === 'Escape') {
                e.stopPropagation();
                setOpenState(false);
                triggerRef.current && triggerRef.current.focus();
            }
        };
        document.addEventListener('mousedown', onDown);
        document.addEventListener('keydown', onKey);
        return () => {
            document.removeEventListener('mousedown', onDown);
            document.removeEventListener('keydown', onKey);
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [open]);

    const close = () => setOpenState(false);
    const toggle = () => setOpenState(!open);

    return (
        <div ref={rootRef} className={`ds-popover ${className}`}>
            {trigger({
                open,
                toggle,
                close,
                props: {
                    ref: triggerRef,
                    onClick: toggle,
                    'aria-expanded': open,
                    'aria-haspopup': 'true',
                    'aria-controls': open ? panelId : undefined,
                },
            })}
            {open && createPortal(
                <div
                    ref={panelRef}
                    id={panelId}
                    role="dialog"
                    aria-label={label}
                    className={`ds-popover__panel ds-popover__panel--${align}${place && place.up ? ' ds-popover__panel--up' : ''}`}
                    style={{
                        position: 'fixed', width: place ? place.width : width,
                        left: place ? place.left : -9999, top: place && !place.up ? place.top : 'auto',
                        bottom: place && place.up ? place.bottom : 'auto', right: 'auto',
                        maxHeight: place ? Math.min(420, place.room) : undefined,
                        visibility: place ? 'visible' : 'hidden',
                        // inside a dialog the panel must stay above it
                        zIndex: rootRef.current && rootRef.current.closest('[role="dialog"], .ds-dialog') ? 'calc(var(--z-dialog) + 10)' : 'var(--z-dropdown)',
                    }}
                >
                    {typeof children === 'function' ? children({ close }) : children}
                </div>,
                document.body,
            )}
        </div>
    );
}

/** Menu list item used inside a Popover. */
export function MenuItem({ icon, children, onClick, danger, href, as: As = 'button', ...rest }) {
    const Comp = href ? 'a' : As;
    return (
        <Comp
            type={Comp === 'button' ? 'button' : undefined}
            href={href}
            className={`ds-menu-item ${danger ? 'is-danger' : ''}`}
            onClick={onClick}
            {...rest}
        >
            {icon && <span className="ds-menu-item__icon">{icon}</span>}
            <span className="ds-menu-item__label">{children}</span>
        </Comp>
    );
}
